'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { T } from './V2Tokens';
import { STATS } from './V1Data';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export function V2Strip() {
    const isMobile = useIsMobile();
    const statsRef = useRef<HTMLElement>(null);

    useGSAP(() => {
        if (!statsRef.current || isMobile) return;

        // Header text reveal
        const headerEls = [
            statsRef.current.querySelector('h2'),
            statsRef.current.querySelector('p'),
        ].filter(Boolean) as Element[];
        gsap.fromTo(headerEls,
            { y: 30, opacity: 0 },
            { y: 0, opacity: 1, stagger: 0.12, duration: 0.7, ease: 'power3.out',
              scrollTrigger: { trigger: statsRef.current, start: 'top 82%' } }
        );

        // Stat cards: scale up from slightly below + faded
        const statEls = statsRef.current.querySelectorAll('.v2-stat');
        gsap.fromTo(statEls,
            { y: 52, opacity: 0, scale: 0.92 },
            { y: 0, opacity: 1, scale: 1, stagger: 0.13, duration: 0.72, ease: 'power3.out',
              scrollTrigger: { trigger: statsRef.current, start: 'top 74%' } }
        );

        // Glow blob parallax — moves opposite to scroll for depth
        gsap.to('.v2-strip-glow', {
            y: 90, ease: 'none',
            scrollTrigger: {
                trigger: statsRef.current,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1.8,
            },
        });
    }, { scope: statsRef, dependencies: [isMobile] });

    if (isMobile) {
        return (
            <section
                key="strip-mobile"
                style={{
                    padding: '48px 20px',
                    background: T.ink,
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div aria-hidden="true" style={{
                    position: 'absolute', top: -120, right: '15%',
                    width: 400, height: 400, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(138,124,255,0.28), transparent 65%)',
                    filter: 'blur(60px)', pointerEvents: 'none',
                }} />
                <div style={{ maxWidth: 1240, margin: '0 auto', position: 'relative' }}>
                    <div style={{ textAlign: 'center', marginBottom: 36 }}>
                        <h2 style={{
                            fontSize: 'clamp(36px, 10vw, 56px)',
                            fontWeight: 900, lineHeight: 1.08,
                            margin: 0, letterSpacing: '-0.025em',
                        }}>
                            الأرقام لا تكذب.
                        </h2>
                        <p style={{
                            fontSize: 15, color: 'rgba(255,255,255,0.6)',
                            margin: '12px auto 0', maxWidth: 500,
                        }}>
                            متوسط النتائج عبر عملائنا الفعليين — لا أرقام مُضخّمة.
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                        {STATS.map((s, i) => (
                            <div
                                key={i}
                                dir="rtl"
                                style={{
                                    padding: '28px 20px',
                                    borderRadius: 18,
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.04em', color: '#fff', marginBottom: 10 }}>{s.n}</div>
                                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>{s.l}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, letterSpacing: '0.07em' }}>{s.en.toUpperCase()}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            key="strip-desktop"
            ref={statsRef}
            style={{
                padding: isMobile ? '48px 20px' : '80px 56px',
                background: T.ink,
                color: '#fff',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div
                aria-hidden="true"
                className="v2-strip-glow"
                style={{
                    position: 'absolute',
                    top: -120,
                    right: '15%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(138,124,255,0.28), transparent 65%)',
                    filter: 'blur(70px)',
                    pointerEvents: 'none',
                }}
            />
            <div style={{ maxWidth: 1240, margin: '0 auto', position: 'relative' }}>
                <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 70 }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 4.5vw, 60px)',
                        fontWeight: 900,
                        lineHeight: 1.08,
                        margin: 0,
                        letterSpacing: '-0.025em',
                    }}>
                        الأرقام لا تكذب.
                    </h2>
                    <p style={{
                        fontSize: 17,
                        color: 'rgba(255,255,255,0.6)',
                        marginTop: 14,
                        maxWidth: 500,
                        margin: '14px auto 0',
                    }}>
                        متوسط النتائج عبر عملائنا الفعليين — لا أرقام مُضخّمة.
                    </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14 }}>
                    {STATS.map((s, i) => (
                        <div
                            key={i}
                            className="v2-stat"
                            style={{
                                padding: '36px 28px',
                                borderRadius: 22,
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                transition: 'transform 0.28s ease, background 0.28s ease, border-color 0.28s ease',
                                cursor: 'default',
                            }}
                            dir="rtl"
                            onMouseEnter={e => {
                                const el = e.currentTarget as HTMLElement;
                                el.style.transform = 'translateY(-4px)';
                                el.style.background = 'rgba(255,255,255,0.07)';
                                el.style.borderColor = 'rgba(255,255,255,0.16)';
                            }}
                            onMouseLeave={e => {
                                const el = e.currentTarget as HTMLElement;
                                el.style.transform = 'none';
                                el.style.background = 'rgba(255,255,255,0.04)';
                                el.style.borderColor = 'rgba(255,255,255,0.08)';
                            }}
                        >
                            <div style={{ fontSize: 66, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.04em', color: '#fff', marginBottom: 14 }}>{s.n}</div>
                            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>{s.l}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6, letterSpacing: '0.07em' }}>{s.en.toUpperCase()}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
